import { useState, useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getProjectDevices, updateDeviceInstance, deleteDeviceInstance } from '../api/deviceInstances'
import { getCableSpecification } from '../api/specification'
import { getRooms } from '../api/rooms'
import { getElectricalPanels, type ElectricalPanel } from '../api/electricalPanels'
import type { DeviceInstance } from '../types'
import { apiClient } from '../api/client'

interface CableInstancesProps {
  projectId: number
  electricalPanelId?: number
  specificationId?: number
  hideCableColumn?: boolean
}

export default function CableInstances({ projectId, electricalPanelId, specificationId, hideCableColumn = false }: CableInstancesProps) {
  const [selectedInstance, setSelectedInstance] = useState<DeviceInstance | null>(null)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [showEditForm, setShowEditForm] = useState(false)
  const [copyInstance, setCopyInstance] = useState<DeviceInstance | null>(null)
  const [mouseDownTarget, setMouseDownTarget] = useState<EventTarget | null>(null)
  const [selectedFromRoom, setSelectedFromRoom] = useState<string>('25')
  const [selectedToRoom, setSelectedToRoom] = useState<string>('')
  const [selectedSpecification, setSelectedSpecification] = useState<string>('')
  const [editFromRoom, setEditFromRoom] = useState<string>('')
  const [editToRoom, setEditToRoom] = useState<string>('')

  // Sorting state
  const [sortField, setSortField] = useState<keyof DeviceInstance | 'position_label' | 'spec_name' | 'route' | 'length' | 'panel_proj_id'>('id')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')

  const queryClient = useQueryClient()

  // Sorting functions
  const handleSort = (field: keyof DeviceInstance | 'position_label' | 'spec_name' | 'route' | 'length' | 'id' | 'panel_proj_id') => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  // Sortable header component
  const SortableHeader = ({ field, children }: { field: keyof DeviceInstance | 'position_label' | 'spec_name' | 'route' | 'length' | 'id' | 'panel_proj_id', children: React.ReactNode }) => (
    <th 
      className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors select-none"
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center space-x-1">
        <span>{children}</span>
        <span className="flex flex-col">
          <span className={`text-xs ${sortField === field && sortDirection === 'asc' ? 'text-blue-600' : 'text-gray-300'}`}>▲</span>
          <span className={`text-xs ${sortField === field && sortDirection === 'desc' ? 'text-blue-600' : 'text-gray-300'}`}>▼</span>
        </span>
      </div>
    </th>
  )

  // Auto-select room based on position label
  const parsePositionLabel = (positionLabel: string) => {
    // Parse format "KLD-10.1-1" or "С-11.1-1" to extract room number "10.1" or "11.1"
    const match = positionLabel.match(/^[A-ZА-Я]+-(\d+\.?\d*)-\d+$/i)
    if (match) {
      const roomNum = match[1]
      const room = rooms.find(r => r.num === roomNum)
      if (room) {
        setSelectedToRoom(room.id.toString())
      }
    }
  }

  // Auto-select room for edit modal
  const parsePositionLabelEdit = (positionLabel: string) => {
    // Parse format "KLD-10.1-1" or "С-11.1-1" to extract room number "10.1" or "11.1"
    const match = positionLabel.match(/^[A-ZА-Я]+-(\d+\.?\d*)-\d+$/i)
    if (match) {
      const roomNum = match[1]
      const room = rooms.find(r => r.num === roomNum)
      if (room) {
        setEditToRoom(room.id.toString())
      }
    }
  }

  // Close modals on ESC key
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowCreateForm(false)
        setShowEditForm(false)
        setCopyInstance(null)
        setSelectedInstance(null)
      }
    }

    document.addEventListener('keydown', handleEscKey)
    return () => {
      document.removeEventListener('keydown', handleEscKey)
    }
  }, [])

  const closeAllModals = () => {
    setShowCreateForm(false)
    setShowEditForm(false)
    setCopyInstance(null)
    setSelectedInstance(null)
    setMouseDownTarget(null)
    setSelectedFromRoom('25')
    setSelectedToRoom('')
    setSelectedSpecification('')
    setEditFromRoom('')
    setEditToRoom('')
  }

  // Load cable instances for the project
  const { data: instances = [], isLoading: instancesLoading, error: instancesError } = useQuery({
    queryKey: ['cable-instances', projectId, electricalPanelId, specificationId],
    queryFn: async () => {
      return await getProjectDevices(projectId, electricalPanelId, 'cable', specificationId)
    },
    enabled: !!projectId
  })

  // Load available specifications for cables
  const { data: specifications = [], isLoading: specificationsLoading } = useQuery({
    queryKey: ['cable-specs', projectId, electricalPanelId],
    queryFn: () => getCableSpecification(projectId, electricalPanelId),
    enabled: !!projectId && (showCreateForm || !!copyInstance)
  })

  // Load rooms for project
  const { data: rooms = [] } = useQuery({
    queryKey: ['rooms', projectId],
    queryFn: () => getRooms(projectId),
    enabled: !!projectId
  })

  const { data: electricalPanels = [], isLoading: electricalPanelsLoading } = useQuery({
    queryKey: ['electricalPanels', projectId],
    queryFn: () => getElectricalPanels(projectId),
    enabled: !!projectId && showEditForm,
  })

  // Create instance mutation
  const createInstanceMutation = useMutation({
    mutationFn: async ({ projectId, specificationId, positionLabel, fromLocation, toLocation, length, electricalPanelId, isReserve }: { 
      projectId: number; 
      specificationId: number; 
      positionLabel: string;
      fromLocation: string;
      toLocation: string;
      length: number;
      electricalPanelId?: number;
      isReserve: boolean;
    }) => {
      // Create the instance with all data at once
      const requestData: any = {
        project_id: projectId,
        specification_id: specificationId,
        position_label: positionLabel,
        is_reserve: isReserve ? 1 : 0,
        room_from: parseInt(fromLocation),
        room_to: parseInt(toLocation),
        length_meters: length
      }

      // Add electrical_panel_id if provided
      if (electricalPanelId) {
        requestData.electrical_panel_id = electricalPanelId
      }

      const response = await apiClient.post('/api/device_instances', requestData) as any;
      return response as DeviceInstance;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cable-instances', projectId, electricalPanelId] })
      setShowCreateForm(false)
      setCopyInstance(null)
    },
    onError: (error) => {
      console.error('❌ Failed to create cable instance:', error)
      alert(`Ошибка создания экземпляра кабеля: ${error instanceof Error ? error.message : 'Неизвестная ошибка'}`)
    }
  })

  // Update instance mutation
  const updateInstanceMutation = useMutation({
    mutationFn: async (data: { id: number; updates: Partial<DeviceInstance> }) => {
      await updateDeviceInstance(data.id, data.updates)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cable-instances', projectId, electricalPanelId] })
      setShowEditForm(false)
      setSelectedInstance(null)
    }
  })

  // Delete instance mutation
  const deleteInstanceMutation = useMutation({
    mutationFn: deleteDeviceInstance,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cable-instances', projectId, electricalPanelId] })
      setSelectedInstance(null)
    }
  })

  const handleCreateInstance = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    
    const formData = new FormData(e.currentTarget)
    const positionLabel = formData.get('position_label') as string
    const length = parseFloat(formData.get('length') as string) || 0
    
    if (!selectedSpecification) {
      alert('Пожалуйста, выберите тип кабеля')
      return
    }
    
    if (!positionLabel.trim()) {
      alert('Пожалуйста, введите позиционное обозначение')
      return
    }

    if (!selectedFromRoom) {
      alert('Пожалуйста, выберите помещение "Откуда"')
      return
    }

    if (!selectedToRoom) {
      alert('Пожалуйста, выберите помещение "Куда"')
      return
    }

    const instanceData = {
      projectId: projectId,
      specificationId: parseInt(selectedSpecification),
      positionLabel: positionLabel.trim(),
      // Swap the logic: selectedFromRoom (new "from") becomes toLocation (old "to")
      // selectedToRoom (new "to") becomes fromLocation (old "from")
      fromLocation: selectedToRoom,
      toLocation: selectedFromRoom,
      length: length,
      electricalPanelId: electricalPanelId,
      isReserve: formData.get('is_reserve') === 'on'
    }

    createInstanceMutation.mutate(instanceData)
  }

  const handleEditInstance = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!selectedInstance) return
    
    const formData = new FormData(e.currentTarget)
    
    const positionLabel = formData.get('position_label') as string
    const description = formData.get('description') as string
    const length = parseFloat(formData.get('length') as string) || 0
    const electricalPanelIdFromForm = formData.get('electrical_panel_id') as string

    if (!electricalPanelIdFromForm) {
      alert('Пожалуйста, выберите электрический щит')
      return
    }
    
    const updates = {
      position_label: positionLabel.trim(),
      description: description.trim(),
      electrical_panel_id: parseInt(electricalPanelIdFromForm, 10),
      is_reserve: formData.get('is_reserve') === 'on' ? 1 : 0,
      // Swap the logic: editFromRoom (new "from") becomes room_to (old "to")
      // editToRoom (new "to") becomes room_from (old "from")
      room_from: editToRoom ? parseInt(editToRoom) : undefined,
      room_to: editFromRoom ? parseInt(editFromRoom) : undefined,
      length_meters: length || undefined,
      // Also keep properties for backward compatibility
      properties: JSON.stringify({
        from_location: editToRoom,
        to_location: editFromRoom,
        length: length
      })
    }

    updateInstanceMutation.mutate({ id: selectedInstance.id, updates })
  }

  const handleEditClick = (instance: DeviceInstance) => {
    setSelectedInstance(instance)
    
    // Load room info using the new function
    loadEditRoomInfo(instance)
    
    setShowEditForm(true)
  }

  // Load room info for copy form
  const loadCopyRoomInfo = (instance: DeviceInstance) => {
    // First try to get from database fields
    // Swap the logic: room_from (old "from") becomes selectedToRoom (new "to")
    // room_to (old "to") becomes selectedFromRoom (new "from")
    if (instance.room_from && instance.room_to) {
      setSelectedFromRoom(instance.room_to.toString())
      setSelectedToRoom(instance.room_from.toString())
      return
    }
    
    // Fallback to properties
    if (!instance.properties) return
    
    try {
      const props = instance.properties ? JSON.parse(instance.properties) : {}
      if (props.from_location && props.to_location) {
        setSelectedFromRoom(props.to_location)
        setSelectedToRoom(props.from_location)
      }
    } catch {
      // Invalid JSON
    }
  }

  const handleCopyClick = (instance: DeviceInstance) => {
    setCopyInstance(instance)
    setSelectedSpecification(instance.specification_id?.toString() || '')
    
    // Load room info using the new function
    loadCopyRoomInfo(instance)
    
    setShowCreateForm(true)
  }

  const handleDeleteClick = (instance: DeviceInstance) => {
    if (confirm(`Удалить экземпляр кабеля "${instance.position_label}"?`)) {
      deleteInstanceMutation.mutate(instance.id)
    }
  }

  // Get route display from database fields or properties fallback
  const getRouteDisplay = (instance: DeviceInstance) => {
    // First try to get from database fields
    // Swap the logic: room_from (old "from") is now displayed as "to"
    // room_to (old "to") is now displayed as "from"
    if (instance.room_from && instance.room_to) {
      const fromRoom = rooms.find(r => r.id === instance.room_to)
      const toRoom = rooms.find(r => r.id === instance.room_from)
      
      if (fromRoom && toRoom) {
        return `${fromRoom.num} → ${toRoom.num}`
      }
    }
    
    // Fallback to properties
    if (!instance.properties) return '—'
    
    try {
      const props = instance.properties ? JSON.parse(instance.properties) : {}
      if (props.from_location && props.to_location) {
        const fromRoom = rooms.find(r => r.id.toString() === props.to_location)
        const toRoom = rooms.find(r => r.id.toString() === props.from_location)
        
        if (fromRoom && toRoom) {
          return `${fromRoom.num} → ${toRoom.num}`
        }
      }
    } catch {
      // Invalid JSON
    }
    
    return '—'
  }

  const getLengthDisplay = (instance: DeviceInstance) => {
    // First try to get from database field
    if (instance.length_meters) {
      return `${instance.length_meters} м`
    }
    
    // Fallback to properties
    if (!instance.properties) return '—'
    
    try {
      const props = instance.properties ? JSON.parse(instance.properties) : {}
      return props.length ? `${props.length} м` : '—'
    } catch {
      return '—'
    }
  }

  // Load room info for edit form
  const loadEditRoomInfo = (instance: DeviceInstance) => {
    // First try to get from database fields
    // Swap the logic: room_from (old "from") becomes editToRoom (new "to")
    // room_to (old "to") becomes editFromRoom (new "from")
    if (instance.room_from && instance.room_to) {
      setEditFromRoom(instance.room_to.toString())
      setEditToRoom(instance.room_from.toString())
      return
    }
    
    // Fallback to properties
    if (!instance.properties) return
    
    try {
      const props = instance.properties ? JSON.parse(instance.properties) : {}
      if (props.from_location && props.to_location) {
        setEditFromRoom(props.to_location)
        setEditToRoom(props.from_location)
      }
    } catch {
      // Invalid JSON
    }
  }

  // Sorted instances
  const sortedInstances = useMemo(() => {
    return [...instances].sort((a, b) => {
      let aValue: any
      let bValue: any

      if (sortField === 'id') {
        aValue = a.id
        bValue = b.id
      } else if (sortField === 'position_label') {
        aValue = a.position_label || ''
        bValue = b.position_label || ''
      } else if (sortField === 'spec_name') {
        aValue = a.spec_name || ''
        bValue = b.spec_name || ''
      } else if (sortField === 'panel_proj_id') {
        aValue = a.panel_proj_id || ''
        bValue = b.panel_proj_id || ''
      } else if (sortField === 'route') {
        aValue = getRouteDisplay(a)
        bValue = getRouteDisplay(b)
      } else if (sortField === 'length') {
        aValue = a.length_meters || 0
        bValue = b.length_meters || 0
      } else {
        aValue = a[sortField]
        bValue = b[sortField]
      }

      // Handle different data types
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase()
        bValue = bValue.toLowerCase()
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1
      return 0
    })
  }, [instances, sortField, sortDirection, getRouteDisplay, getLengthDisplay])

  if (instancesError) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Экземпляры кабелей</h2>
        <div className="text-red-600 bg-red-50 p-4 rounded-md">
          Ошибка загрузки экземпляров кабелей: {instancesError instanceof Error ? instancesError.message : 'Неизвестная ошибка'}
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-gray-900">🔌 Экземпляры кабелей</h2>
        <button
          onClick={() => {
            setCopyInstance(null)
            setShowCreateForm(true)
          }}
          className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
        >
          ➕ Добавить экземпляр
        </button>
      </div>

      {/* Loading state */}
      {instancesLoading && (
        <div className="text-center py-8 text-gray-500">Загрузка экземпляров кабелей...</div>
      )}

      {/* Table for desktop */}
      {!instancesLoading && instances.length > 0 && (
        <div className="hidden md:block overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <SortableHeader field="id">ID</SortableHeader>
                <SortableHeader field="position_label">Обозн.</SortableHeader>
                {!hideCableColumn && <SortableHeader field="spec_name">Кабель</SortableHeader>}
                <SortableHeader field="panel_proj_id">Щит</SortableHeader>
                <SortableHeader field="route">Трасса (от → до)</SortableHeader>
                <SortableHeader field="length">Длина (м)</SortableHeader>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Действия
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {sortedInstances.map((instance) => (
                <tr key={instance.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {instance.id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    <Link
                      to={`/projects/${projectId}/device-instances/${instance.id}`}
                      className="text-blue-600 hover:text-blue-800 hover:underline"
                    >
                      {instance.position_label || '—'}
                    </Link>
                  </td>
                  {!hideCableColumn && (
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {instance.spec_name}
                    </td>
                  )}
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {instance.panel_proj_id || '—'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {getRouteDisplay(instance)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {getLengthDisplay(instance)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleEditClick(instance)}
                        className="text-blue-600 hover:text-blue-800"
                        title="Редактировать"
                      >
                        ✏️
                      </button>
                      <button
                        onClick={() => handleCopyClick(instance)}
                        className="text-green-600 hover:text-green-800"
                        title="Копировать"
                      >
                        📋
                      </button>
                      <button
                        onClick={() => handleDeleteClick(instance)}
                        className="text-red-600 hover:text-red-800"
                        title="Удалить"
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Mobile cards */}
      {!instancesLoading && instances.length > 0 && (
        <div className="md:hidden space-y-4">
          {sortedInstances.map((instance) => (
            <div key={instance.id} className="border rounded-lg p-4 bg-gray-50">
              <div className="flex justify-between items-start mb-2">
                <div>
                  <h3 className="font-medium text-gray-900">
                    <span className="text-sm text-gray-500">#{instance.id}</span>{' '}
                    <Link
                      to={`/projects/${projectId}/device-instances/${instance.id}`}
                      className="text-blue-600 hover:text-blue-800 hover:underline"
                    >
                      {instance.position_label || '—'}
                    </Link>
                  </h3>
                  {!hideCableColumn && <p className="text-sm text-gray-600">{instance.spec_name}</p>}
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleEditClick(instance)}
                    className="text-blue-600 hover:text-blue-800"
                    title="Редактировать"
                  >
                    ✏️
                  </button>
                  <button
                    onClick={() => handleCopyClick(instance)}
                    className="text-green-600 hover:text-green-800"
                    title="Копировать"
                  >
                    📋
                  </button>
                  <button
                    onClick={() => handleDeleteClick(instance)}
                    className="text-red-600 hover:text-red-800"
                    title="Удалить"
                  >
                    🗑️
                  </button>
                </div>
              </div>
              
              <div className="space-y-1 text-sm text-gray-600">
                <div><strong>Щит:</strong> {instance.panel_proj_id || '—'}</div>
                <div><strong>Трасса:</strong> {getRouteDisplay(instance)}</div>
                <div><strong>Длина:</strong> {getLengthDisplay(instance)}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Empty state */}
      {!instancesLoading && instances.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          <p className="mb-3">Экземпляры кабелей не созданы</p>
          <button
            onClick={() => {
              setCopyInstance(null)
              setSelectedFromRoom('25')
              setSelectedToRoom('')
              setSelectedSpecification('')
              setShowCreateForm(true)
            }}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
          >
            Создать первый экземпляр
          </button>
        </div>
      )}

      {/* Create Cable Instance Modal */}
      {showCreateForm && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onMouseDown={(e) => setMouseDownTarget(e.target)}
          onMouseUp={(e) => {
            if (e.target === mouseDownTarget && e.target === e.currentTarget) {
              closeAllModals()
            }
          }}
        >
          <div className="bg-white rounded-lg w-full max-w-[70rem] mx-4 max-h-[90vh] flex flex-col">
            <div className="p-6 pb-4 border-b border-gray-200 flex-shrink-0">
              <h3 className="text-lg font-semibold">
                {copyInstance ? 'Копировать экземпляр кабеля' : 'Создать экземпляр кабеля'}
              </h3>
            </div>
            
            <div className="flex-1 overflow-y-auto p-6 pt-4">
            
            {specificationsLoading ? (
              <div className="text-center py-4">Загрузка спецификаций...</div>
            ) : (
              <form onSubmit={handleCreateInstance} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Тип кабеля <span className="text-red-500">*</span>
                  </label>
                  <input type="hidden" name="specification_id" value={selectedSpecification} />
                  <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto">
                    {specifications.map((spec) => {
                      // Check if spec.name already contains wire_count and section info
                      const hasWireInfo = spec.name.includes('×') || spec.name.includes('x')
                      const displayName = hasWireInfo 
                        ? spec.name 
                        : `${spec.name} ${spec.properties?.wire_count}×${spec.properties?.section}`
                      
                      return (
                        <button
                          key={spec.id}
                          type="button"
                          onClick={() => setSelectedSpecification(spec.id.toString())}
                          className={`px-3 py-1.5 text-sm rounded border transition-colors ${
                            selectedSpecification === spec.id.toString()
                              ? 'bg-indigo-500 text-white border-indigo-500'
                              : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
                          }`}
                          title={`${spec.name} - ${spec.properties?.wire_count} жил × ${spec.properties?.section} мм²`}
                        >
                          {displayName}
                        </button>
                      )
                    })}
                  </div>
                  {selectedSpecification && (
                    <div className="mt-1 text-sm text-gray-600">
                      Выбрано: {specifications.find(s => s.id.toString() === selectedSpecification)?.name}
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-4">
                  <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
                    Позиционное обозначение <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="position_label"
                    required
                    defaultValue={copyInstance?.position_label || ''}
                    placeholder="С-1.1-1"
                    onChange={(e) => parsePositionLabel(e.target.value)}
                    className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Откуда {selectedFromRoom && `(${rooms.find(r => r.id.toString() === selectedFromRoom)?.name})`} <span className="text-red-500">*</span>
                  </label>
                  <input type="hidden" name="from_location" value={selectedFromRoom} />
                  <div className="flex flex-wrap gap-1 max-h-36 overflow-y-auto">
                    {rooms.map((room) => (
                      <button
                        key={room.id}
                        type="button"
                        onClick={() => setSelectedFromRoom(room.id.toString())}
                        className={`px-2 py-1 text-xs rounded border transition-colors ${
                          selectedFromRoom === room.id.toString()
                            ? 'bg-blue-500 !bg-blue-500 !text-white border-blue-500'
                            : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
                        }`}
                        title={room.name}
                      >
                        {room.num}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Куда {selectedToRoom && `(${rooms.find(r => r.id.toString() === selectedToRoom)?.name})`} <span className="text-red-500">*</span>
                  </label>
                  <input type="hidden" name="to_location" value={selectedToRoom} />
                  <div className="flex flex-wrap gap-1 max-h-36 overflow-y-auto">
                    {rooms.map((room) => (
                      <button
                        key={room.id}
                        type="button"
                        onClick={() => setSelectedToRoom(room.id.toString())}
                        className={`px-2 py-1 text-xs rounded border transition-colors ${
                          selectedToRoom === room.id.toString()
                            ? 'bg-green-500 !bg-green-500 !text-white border-green-500'
                            : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
                        }`}
                        title={room.name}
                      >
                        {room.num}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
                    Длина (м)
                  </label>
                  <input
                    type="number"
                    name="length"
                    step="0.1"
                    min="0"
                    placeholder="0"
                    defaultValue={copyInstance ? (() => {
                      // First try to get from database field
                      if (copyInstance.length_meters) {
                        return copyInstance.length_meters.toString()
                      }
                      
                      // Fallback to properties
                      if (copyInstance.properties) {
                        try {
                          const props = JSON.parse(copyInstance.properties)
                          return props.length || ''
                        } catch {
                          return ''
                        }
                      }
                      
                      return ''
                    })() : ''}
                    className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    name="is_reserve"
                    id="is_reserve_create"
                    defaultChecked={(copyInstance as any)?.is_reserve === 1}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="is_reserve_create" className="ml-2 block text-sm text-gray-900">
                    Резервный
                  </label>
                </div>

                <div className="flex space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={closeAllModals}
                    className="flex-1 px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                  >
                    Отмена
                  </button>
                  <button
                    type="submit"
                    disabled={createInstanceMutation.isPending}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                  >
                    {createInstanceMutation.isPending ? 'Создание...' : 'Создать'}
                  </button>
                </div>
              </form>
            )}
            </div>
          </div>
        </div>
      )}

      {/* Edit Cable Instance Modal */}
      {showEditForm && selectedInstance && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onMouseDown={(e) => setMouseDownTarget(e.target)}
          onMouseUp={(e) => {
            if (e.target === mouseDownTarget && e.target === e.currentTarget) {
              closeAllModals()
            }
          }}
        >
          <div className="bg-white rounded-lg w-full max-w-[70rem] mx-4 max-h-[90vh] flex flex-col">
            <div className="p-6 pb-4 border-b border-gray-200 flex-shrink-0">
              <h3 className="text-lg font-semibold">
                Редактировать экземпляр кабеля <span className="instance-id">#{selectedInstance.id}</span>
              </h3>
            </div>
            
            <div className="flex-1 overflow-y-auto p-6 pt-4">
              <form onSubmit={handleEditInstance} className="space-y-6">
              <div className="flex items-center gap-4">
                <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
                  Позиционное обозначение
                </label>
                <input
                  type="text"
                  name="position_label"
                  required
                  defaultValue={selectedInstance.position_label || ''}
                  onChange={(e) => parsePositionLabelEdit(e.target.value)}
                  className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Электрический щит</label>
                {electricalPanelsLoading ? (
                  <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500">
                    Загрузка щитов...
                  </div>
                ) : (
                  <select
                    name="electrical_panel_id"
                    required
                    defaultValue={selectedInstance.electrical_panel_id || ''}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Выберите электрический щит...</option>
                    {(electricalPanels as ElectricalPanel[]).map((panel) => (
                      <option key={panel.id} value={panel.id}>
                        {panel.name} ({panel.proj_id})
                      </option>
                    ))}
                  </select>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Откуда {editFromRoom && `(${rooms.find(r => r.id.toString() === editFromRoom)?.name})`}
                </label>
                <div className="flex flex-wrap gap-1 max-h-36 overflow-y-auto">
                  {rooms.map((room) => (
                    <button
                      key={room.id}
                      type="button"
                      onClick={() => setEditFromRoom(room.id.toString())}
                      className={`px-2 py-1 text-xs rounded border transition-colors ${
                        editFromRoom === room.id.toString()
                          ? 'bg-blue-500 !bg-blue-500 !text-white border-blue-500'
                          : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
                      }`}
                      title={room.name}
                    >
                      {room.num}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Куда {editToRoom && `(${rooms.find(r => r.id.toString() === editToRoom)?.name})`}
                </label>
                <div className="flex flex-wrap gap-1 max-h-36 overflow-y-auto">
                  {rooms.map((room) => (
                    <button
                      key={room.id}
                      type="button"
                      onClick={() => setEditToRoom(room.id.toString())}
                      className={`px-2 py-1 text-xs rounded border transition-colors ${
                        editToRoom === room.id.toString()
                          ? 'bg-green-500 !bg-green-500 !text-white border-green-500'
                          : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
                      }`}
                      title={room.name}
                    >
                      {room.num}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex items-center gap-4">
                <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
                  Описание
                </label>
                <input
                  type="text"
                  name="description"
                  defaultValue={selectedInstance.description || ''}
                  className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Описание кабеля..."
                />
                
                <label className="text-sm font-medium text-gray-700 whitespace-nowrap">
                  Длина (м)
                </label>
                <input
                  type="number"
                  name="length"
                  step="0.1"
                  min="0"
                  defaultValue={(() => {
                    // First try to get from database field
                    if (selectedInstance.length_meters) {
                      return selectedInstance.length_meters.toString()
                    }
                    
                    // Fallback to properties
                    if (selectedInstance.properties) {
                      try {
                        const props = JSON.parse(selectedInstance.properties)
                        return props.length || ''
                      } catch {
                        return ''
                      }
                    }
                    
                    return ''
                  })()}
                  className="w-20 border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    name="is_reserve"
                    id="is_reserve_edit"
                    defaultChecked={(selectedInstance as any).is_reserve === 1}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="is_reserve_edit" className="ml-2 block text-sm text-gray-900">
                    Резервный
                  </label>
                </div>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={closeAllModals}
                  className="flex-1 px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                >
                  Отмена
                </button>
                <button
                  type="submit"
                  disabled={updateInstanceMutation.isPending}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {updateInstanceMutation.isPending ? 'Сохранение...' : 'Сохранить'}
                </button>
              </div>
            </form>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}